'use client'

import { useState } from 'react'
import { Calendar } from '@/ui/calendar'
import { Button } from '@/ui/button'
import { ResponsiveSelector } from '@/ui/responsive-selector'
import { CalendarBlankIcon, XIcon } from '@phosphor-icons/react'
import { FOCUS_STYLES, ICON_SIZES } from '@/ui/design-tokens'
import { cn } from '@/lib/tailwind'

interface DateFieldProps {
  /** Currently selected date */
  value?: Date
  /** Called when date changes (null = cleared) */
  onChange: (date: Date | null) => void
  /** Placeholder text when no date selected */
  placeholder?: string
  /** Whether the clear button is shown */
  clearable?: boolean
  /** Display format for the date */
  formatDate?: (date: Date) => string
  /** Whether the field is disabled */
  disabled?: boolean
  /** Custom className for the trigger button */
  className?: string
  /** Trigger variant: "inline" renders underline-on-hover text, "icon" renders icon+text */
  variant?: 'inline' | 'icon'
}

/**
 * Consolidated date field component.
 * Desktop: Calendar in popover.
 * Mobile: Calendar in bottom sheet drawer with comfortable padding.
 * Replaces DatePicker, DatePropertyPopover, and raw Calendar-in-Popover patterns.
 */
export function DateField({
  value,
  onChange,
  placeholder = '+ date',
  clearable = true,
  formatDate = (d) => d.toLocaleDateString(),
  disabled = false,
  className,
  variant = 'inline',
}: DateFieldProps) {
  const [open, setOpen] = useState(false)

  const handleSelect = (date: Date | undefined) => {
    onChange(date ?? null)
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setOpen(false)
  }

  const trigger =
    variant === 'icon' ? (
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex items-center gap-1',
          'hover:text-foreground transition-colors',
          FOCUS_STYLES.ringCompact,
          'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
      >
        <CalendarBlankIcon className={ICON_SIZES.compact} />
        <span>{value ? formatDate(value) : placeholder}</span>
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'text-muted-foreground hover:text-foreground hover:bg-muted/30 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-sm transition-colors',
          FOCUS_STYLES.ringCompact,
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <CalendarBlankIcon className={ICON_SIZES.compact} />
        <span>{value ? formatDate(value) : placeholder}</span>
      </button>
    )

  return (
    <ResponsiveSelector
      open={open}
      onOpenChange={setOpen}
      ariaLabel="Select date"
      trigger={trigger}
      contentClassName="p-0 w-auto"
    >
      <div className="flex flex-col">
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          initialFocus
        />
        {clearable && value && (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={handleClear}
            >
              <XIcon className={`mr-1 ${ICON_SIZES.compact}`} />
              Clear date
            </Button>
          </div>
        )}
      </div>
    </ResponsiveSelector>
  )
}
